<template>
  <div class="trick-table-panel">
    <div class="container trick-list-content px-0">
      <div class="row mb-3">
        <div class="col-12">
          <div class="form-group">
            <div class="input-group">
              <input
                :value="searchQuery"
                type="text"
                class="form-control"
                :placeholder="searchPlaceholder"
                @input="$emit('update:searchQuery', $event.target.value)"
              >
              <template v-if="isSupervisorUser">
                <button
                  class="btn btn-outline-secondary"
                  type="button"
                  @click.prevent="$emit('open-csv-sync')"
                >
                  <i class="fa-solid fa-code-compare me-1"></i>{{ csvSyncButtonLabel }}
                </button>
                <button
                  class="btn btn-primary"
                  type="button"
                  @click.prevent="$emit('create-trick')"
                >
                  <i class="fa-solid fa-plus me-1"></i>{{ createButtonLabel }}
                </button>
              </template>
            </div>
          </div>
        </div>
      </div>

      <div class="row mb-3">
        <div class="col-6">
          <div class="form-group">
            <label for="categoryFilter">
              {{ categoryFilterLabel }}
              <span class="badge bg-secondary" @click="$emit('reset-filter', 'filterCategory')"><i class="fa-solid fa-broom"></i></span>
            </label>
            <select
              id="categoryFilter"
              :value="filterCategory"
              class="form-control"
              @change="$emit('update:filterCategory', normalizeSelectValue($event.target.value))"
            >
              <option v-for="category in categories" :key="category.id" :value="category.id">{{ category.name }}</option>
            </select>
          </div>
        </div>

        <div class="col-6">
          <div class="form-group">
            <label for="levelFilter">
              {{ levelFilterLabel }}
              <span class="badge bg-secondary" @click="$emit('reset-filter', 'filterLevel')"><i class="fa-solid fa-broom"></i></span>
            </label>
            <select
              id="levelFilter"
              :value="filterLevel"
              class="form-control"
              @change="$emit('update:filterLevel', normalizeSelectValue($event.target.value))"
            >
              <option v-for="level in levels" :key="level.id" :value="level.id">{{ level.name }}</option>
            </select>
          </div>
        </div>
      </div>

      <div class="row">
        <div class="col-12">
          <table class="table table-responsive table-striped table-sm">
            <thead>
              <tr>
                <th @click="$emit('sort-by', 'id')">
                  Id
                  <i :class="sortIconClass('id')"></i>
                </th>
                <th @click="$emit('sort-by', 'name')">
                  {{ nameLabel }}
                  <i :class="sortIconClass('name')"></i>
                </th>
                <th @click="$emit('sort-by', 'value')">
                  {{ valueLabel }}
                  <i :class="sortIconClass('value')"></i>
                </th>
                <th @click="$emit('sort-by', 'level')">
                  {{ levelLabel }}
                  <i :class="sortIconClass('level')"></i>
                </th>
                <th @click="$emit('sort-by', 'category')">
                  {{ categoryLabel }}
                  <i :class="sortIconClass('category')"></i>
                </th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="trick in paginatedData"
                :key="trick.id"
                @click.stop="$emit('select-trick', trick)"
                data-bs-toggle="modal"
                :data-bs-target="trick ? '#infoModal' : ''"
              >
                <td class="text-center">{{ trick.id }}</td>
                <td>{{ trick.name }}</td>
                <td class="text-center">{{ trick.value }}</td>
                <td class="text-center">
                  <div class="level-circle text-center mr-1" :class="getBackgroundColorClass(trick.level_id)">
                    <b>{{ trick.level_id }}</b>
                  </div>
                </td>
                <td class="text-center">{{ categoryName(trick.categorie_id) }}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>

    <div class="trick-table-footer text-center">
      <nav aria-label="Page navigation example" class="d-flex align-content-center justify-content-center">
        <ul class="pagination">
          <li class="page-item" :class="{ disabled: currentPage === 1 }">
            <a class="page-link" href="#" @click.prevent="$emit('change-page', 1)">
              <i class="fa-solid fa-angles-left"></i>
            </a>
          </li>
          <li class="page-item" :class="{ disabled: currentPage === 1 }">
            <a class="page-link" href="#" @click.prevent="$emit('change-page', currentPage - 1)">
              <i class="fa-solid fa-angle-left"></i>
            </a>
          </li>
          <li v-for="pageNumber in limitedPages" :key="pageNumber">
            <a class="page-link" :class="{ 'custom-active': currentPage === pageNumber }" href="#" @click.prevent="$emit('change-page', pageNumber)">
              {{ pageNumber }}
            </a>
          </li>
          <li class="page-item" :class="{ disabled: currentPage === totalPages }">
            <a class="page-link" href="#" @click.prevent="$emit('change-page', currentPage + 1)">
              <i class="fa-solid fa-angle-right"></i>
            </a>
          </li>
          <li class="page-item" :class="{ disabled: currentPage === totalPages }">
            <a class="page-link" href="#" @click.prevent="$emit('change-page', totalPages)">
              <i class="fa-solid fa-angles-right"></i>
            </a>
          </li>
        </ul>
      </nav>
    </div>
  </div>
</template>

<script>
export default {
  name: 'TrickTablePanel',
  props: {
    paginatedData: {
      type: Array,
      default: () => [],
    },
    searchQuery: {
      type: String,
      default: '',
    },
    filterCategory: {
      type: [String, Number],
      default: '',
    },
    filterLevel: {
      type: [String, Number],
      default: '',
    },
    categories: {
      type: Array,
      default: () => [],
    },
    levels: {
      type: Array,
      default: () => [],
    },
    currentPage: {
      type: Number,
      default: 1,
    },
    totalPages: {
      type: Number,
      default: 1,
    },
    limitedPages: {
      type: Array,
      default: () => [],
    },
    sortOrders: {
      type: Object,
      default: () => ({}),
    },
    isSupervisorUser: {
      type: Boolean,
      default: false,
    },
    categoryName: {
      type: Function,
      required: true,
    },
    getBackgroundColorClass: {
      type: Function,
      required: true,
    },
    labels: {
      type: Object,
      default: () => ({}),
    },
  },
  emits: ['update:searchQuery', 'update:filterCategory', 'update:filterLevel', 'sort-by', 'change-page', 'reset-filter', 'select-trick', 'create-trick', 'open-csv-sync'],
  computed: {
    searchPlaceholder() {
      return this.labels.search || '';
    },
    createButtonLabel() {
      return this.labels.newTrick || 'Nuevo truco';
    },
    csvSyncButtonLabel() {
      return this.labels.csvSync || 'Comparar CSV';
    },
    categoryFilterLabel() {
      return this.labels.categoryFilter || 'Categoria';
    },
    levelFilterLabel() {
      return this.labels.levelFilter || 'Nivel';
    },
    nameLabel() {
      return this.labels.name || 'Nombre';
    },
    valueLabel() {
      return this.labels.value || 'Valor';
    },
    levelLabel() {
      return this.labels.level || 'Nivel';
    },
    categoryLabel() {
      return this.labels.category || 'Categoria';
    },
  },
  methods: {
    normalizeSelectValue(value) {
      if (value === '') return '';
      const normalized = Number(value);
      return Number.isFinite(normalized) ? normalized : value;
    },
    sortIconClass(key) {
      const order = this.sortOrders?.[key] === 1 ? 'up' : 'down';
      return ['fa-solid', `fa-arrow-${order}-short-wide`, 'fa-sm'];
    },
  },
};
</script>

<style scoped>
.trick-table-panel {
  min-width: 0;
}

.trick-list-content {
  max-width: 100%;
}

.table th {
  white-space: nowrap;
}

.table th i {
  margin-left: 5px;
  vertical-align: middle;
}

.table td {
  vertical-align: middle;
}

.table tbody tr {
  cursor: pointer;
}

.table th:nth-child(1),
.table th:nth-child(2),
.table th:nth-child(3),
.table th:nth-child(4),
.table th:nth-child(5) {
  cursor: pointer;
}

.trick-table-footer {
  padding-top: 16px;
}

.pagination {
  margin: 0;
}

.custom-active {
  background-color: #6c757d;
  border-color: #6c757d;
  color: #fff;
}

.level-circle {
  width: 30px;
  height: 30px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  border: 1px solid #000;
}

.color-level-1 {
  background-color: #084cff;
}

.color-level-2 {
  background-color: #17e700;
}

.color-level-3 {
  background-color: #ff7e00;
}

.color-level-4 {
  background-color: #ff0000;
}

@media (max-width: 576px) {
  .table th:nth-child(4),
  .table td:nth-child(4),
  .table th:nth-child(5),
  .table td:nth-child(5) {
    display: none;
  }
}
</style>